'use client';

import { Button, LoadingOverlay } from '@payloadcms/ui';
import { ReactNode, useCallback, useEffect, useState } from 'react';

export interface GoogleSheetsConnectionStatus {
  connected: boolean;
  hasRefreshToken?: boolean;
  email?: string | null;
}

interface GoogleSheetsConnectionManagerProps {
  children: (props: { status: GoogleSheetsConnectionStatus; connect: () => void }) => ReactNode;
  loadingText?: string;
}

const GoogleSheetsConnectionManager = ({
  children,
  loadingText = 'Checking Google Sheets connection...',
}: GoogleSheetsConnectionManagerProps) => {
  const [status, setStatus] = useState<GoogleSheetsConnectionStatus>({ connected: false });
  const [isChecking, setIsChecking] = useState(true);

  const checkConnection = useCallback(async () => {
    try {
      setIsChecking(true);
      const res = await fetch('/api/google-sheets/status');
      const data: GoogleSheetsConnectionStatus = await res.json();
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    checkConnection();
  }, [checkConnection]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'GOOGLE_SHEETS_CONNECTED') {
        checkConnection();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkConnection]);

  const connect = useCallback(() => {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    window.open('/api/google-sheets/authorize', 'google-sheets-auth', `width=${width},height=${height},left=${left},top=${top},popup=yes`);
  }, []);

  if (isChecking) {
    return <LoadingOverlay loadingText={loadingText} />;
  }

  if (!status.connected) {
    return (
      <div>
        <h2>Connect to Google Sheets</h2>
        <p>To continue, you need to sign in with Google.</p>
        <Button onClick={connect} buttonStyle="primary">
          Sign in with Google
        </Button>
      </div>
    );
  }

  return <>{children({ status, connect })}</>;
};

export default GoogleSheetsConnectionManager;
